import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NetExternalApproval {
  id: string;
  boletim_id: string;
  user_id: string | null;
  status: 'aprovado' | 'reprovado' | 'contestado';
  observacao: string | null;
  ip_address: string | null;
  device_fingerprint: string | null;
  email_destinatario: string | null;
  origem: 'email_token' | 'sistema_interno';
  token_id: string | null;
  created_at: string;
}

export function useNetExternalApprovals(boletimId?: string) {
  return useQuery({
    queryKey: ['net-external-approvals', boletimId],
    queryFn: async () => {
      let query = supabase
        .from('net_external_approvals')
        .select('*')
        .order('created_at', { ascending: false });

      if (boletimId) {
        query = query.eq('boletim_id', boletimId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as NetExternalApproval[];
    },
    enabled: !!boletimId,
  });
}

export function useCreateExternalApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      boletim_id: string;
      status: 'aprovado' | 'reprovado' | 'contestado';
      observacao?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Não autenticado');

      const { data, error } = await supabase
        .from('net_external_approvals')
        .insert({
          boletim_id: params.boletim_id,
          user_id: user.id,
          status: params.status,
          observacao: params.observacao || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['net-external-approvals', vars.boletim_id] });
      queryClient.invalidateQueries({ queryKey: ['net-governance-ativa'] });
    },
  });
}
